import { type PainPointKind, type PainPointStatus } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Frown } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import { PAIN_KIND } from "@/components/domain/analytics";
import { PageHeader } from "@/components/domain/misc";
import { ErrorPanel, Page } from "@/components/layout/page";
import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/input";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { FilterPill } from "@/features/coverage/filter-pill";
import { PAIN_STATUSES, PAIN_STATUS_LABEL, PainPointCard } from "@/features/pain-points/pain-point-card";
import { q } from "@/lib/api";
import { fmtInt, fmtMoney } from "@/lib/format";

const KINDS = Object.keys(PAIN_KIND) as PainPointKind[];

export const Route = createFileRoute("/_app/pain-points")({
  validateSearch: z.object({
    kind: z.enum(KINDS as [PainPointKind, ...PainPointKind[]]).optional().catch(undefined),
    agentId: z.string().min(1).optional().catch(undefined),
    status: z.enum(PAIN_STATUSES as [PainPointStatus, ...PainPointStatus[]]).optional().catch(undefined),
  }),
  component: PainPointsPage,
});

function PainPointsPage() {
  const { kind, agentId, status } = Route.useSearch();
  const navigate = Route.useNavigate();
  // Kind is filtered client-side so the pills can show counts; the API ranks by |success impact| × volume.
  const list = useQuery(q.painPoints({ agentId, status }));
  const agents = useQuery(q.agents());
  const useCases = useQuery(q.useCases());
  const agentNames = useMemo(() => new Map(agents.data?.map((a) => [a.id, a.name])), [agents.data]);
  const ucNames = useMemo(() => new Map(useCases.data?.map((u) => [u.id, u.name])), [useCases.data]);
  const counts = useMemo(() => {
    const m = new Map<PainPointKind, number>();
    for (const p of list.data ?? []) m.set(p.kind, (m.get(p.kind) ?? 0) + 1);
    return m;
  }, [list.data]);
  const rows = useMemo(() => (kind ? list.data?.filter((p) => p.kind === kind) : list.data), [list.data, kind]);
  const set = (patch: Partial<{ kind: PainPointKind; agentId: string; status: PainPointStatus }>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false });
  const totalSessions = rows?.reduce((s, p) => s + p.sessions7d, 0) ?? 0;
  const totalCost = rows?.reduce((s, p) => s + p.costImpact7d.amount, 0) ?? 0;

  return (
    <Page>
      <PageHeader
        title="Pain points"
        description="Recurring reasons sessions go badly, discovered from friction, unanswered questions, unsupported requests and failed sessions. Ranked by success impact × volume."
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Filter by kind" className="flex flex-wrap gap-1.5">
          <FilterPill active={!kind} onClick={() => set({ kind: undefined })} label="All kinds" count={list.data?.length ?? 0} />
          {KINDS.map((k) => (
            <FilterPill key={k} active={kind === k} onClick={() => set({ kind: kind === k ? undefined : k })} label={PAIN_KIND[k].label} count={counts.get(k) ?? 0} />
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select aria-label="Agent" value={agentId ?? ""} onChange={(e) => set({ agentId: e.target.value || undefined })} className="w-48">
            <option value="">All agents</option>
            {agents.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <Select aria-label="Status" value={status ?? ""} onChange={(e) => set({ status: (e.target.value || undefined) as PainPointStatus | undefined })} className="w-40">
            <option value="">Open & tracked</option>
            {PAIN_STATUSES.map((s) => (
              <option key={s} value={s}>
                {PAIN_STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {rows?.length ? (
        <p className="mb-3 text-xs text-fg-3">
          {rows.length} pain points · <span className="tabular text-fg-2">{fmtInt(totalSessions)}</span> affected sessions and <span className="tabular text-fg-2">{fmtMoney(totalCost, { compact: true })}</span> estimated cost in the last 7 days
        </p>
      ) : null}

      {list.error ? (
        <ErrorPanel error={list.error} reset={() => void list.refetch()} />
      ) : !rows ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      ) : rows.length ? (
        <ol className="flex flex-col gap-3">
          {rows.map((p, i) => (
            <li key={p.id}>
              <PainPointCard p={p} rank={i + 1} agentNames={agentNames} useCaseNames={ucNames} />
            </li>
          ))}
        </ol>
      ) : (
        <Card>
          <EmptyState icon={<Frown />} title="No pain points match" description={kind || agentId || status ? "Try another kind, agent or status." : "Nothing recurring stands out yet. Discovery runs nightly over failed, escalated and frustrated sessions."} />
        </Card>
      )}
    </Page>
  );
}
